/**
 * Route layout: one content tree (<Outlet/>), two kinds of chrome.
 *
 * Below `md` the MobileShell (header + bottom nav + "More" sheet) wraps the
 * page; from `md` up the shared AppShell sidebar does. The switch is driven by
 * a media query rather than CSS visibility so the page component — and its
 * form state and queries — is mounted exactly once.
 *
 * Toasts and the offline sync indicator share one fixed column (StatusStack)
 * that sits above the bottom nav on phones, so they never cover it or each
 * other.
 */
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { AppShell, useDarkMode } from '@pazportal/ui';
import { useAuth } from '../contexts/AuthContext';
import { navSections, PORTAL_COLOR, PORTAL_NAME, PORTAL_SUBTITLE } from '../config/navigation';
import { OfflineIndicator } from './OfflineIndicator';
import { MobileShell, MOBILE_BOTTOM_OFFSET } from './MobileShell';
import { ToastViewport, useToast } from './Toaster';
import { useActiveTrip } from '../hooks/useActiveTrip';
import { useIsDesktop } from '../hooks/useMediaQuery';
import { hrefWithTrip } from '../lib/tripQuery';
import { errorMessage } from '../lib/errors';
import { humanize, initialsFrom } from '../lib/formatters';

function StatusStack({ bottom }: { bottom: string }) {
  return (
    <div
      className="pointer-events-none fixed inset-x-0 z-[60] flex flex-col items-center gap-2 px-3"
      style={{ bottom }}
    >
      <ToastViewport />
      <OfflineIndicator />
    </div>
  );
}

export function AppLayout() {
  const { user, role, profileName, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { isDark, toggle } = useDarkMode();
  const { data: trip } = useActiveTrip();
  const isDesktop = useIsDesktop();
  const { notify } = useToast();

  const userLabel = profileName ?? user?.email ?? 'Signed in';
  const userInitials = initialsFrom(profileName ?? user?.email);

  const siteName = trip?.location?.name ?? trip?.customer?.name ?? null;
  const title = siteName ?? 'No trip selected';
  const subtitle = trip
    ? `${trip.project_number}${trip.status ? ` · ${humanize(trip.status)}` : ''}`
    : 'Choose a field trip to begin';

  const hrefFor = (href: string) => hrefWithTrip(href, trip?.id);
  const go = (href: string) => navigate(hrefFor(href));

  const onSignOut = async () => {
    try {
      await signOut();
    } catch (e) {
      notify(`Sign-out failed: ${errorMessage(e)}`, 'error');
    }
  };

  const content = <Outlet />;

  if (isDesktop) {
    return (
      <>
        <AppShell
          navSections={navSections}
          portalColor={PORTAL_COLOR}
          portalName={PORTAL_NAME}
          portalSubtitle={PORTAL_SUBTITLE}
          propertyName={title}
          propertySubtitle={subtitle}
          propertyInitials={siteName ? initialsFrom(siteName) : 'OS'}
          userName={userLabel}
          userRole={role ? humanize(role) : undefined}
          userInitials={userInitials}
          currentPath={location.pathname}
          onNavigate={go}
          onSignOut={() => void onSignOut()}
          onDarkModeToggle={toggle}
          isDarkMode={isDark}
        >
          {content}
        </AppShell>
        <StatusStack bottom="1rem" />
      </>
    );
  }

  return (
    <>
      <MobileShell
        title={title}
        subtitle={subtitle}
        userLabel={userLabel}
        currentPath={location.pathname}
        hrefFor={hrefFor}
        onNavigate={go}
        onSignOut={() => void onSignOut()}
        isDark={isDark}
        onToggleDark={toggle}
      >
        {content}
      </MobileShell>
      <StatusStack bottom={`calc(${MOBILE_BOTTOM_OFFSET} + 0.75rem)`} />
    </>
  );
}
